import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Avatar,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InventoryIcon from "@mui/icons-material/Inventory";
import PhoneIcon from "@mui/icons-material/Phone";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { C, SCROLL } from "../theme";
import { STATUS_LABELS, TECHNICIAN_STATUS } from "../constants";
import TechnicianExpedientePanel from "./TechnicianExpedientePanel";
import TechnicianScoresChart from "./TechnicianScoresChart";
import technicianExpedienteService from "../../../api/technicianExpedienteService";

const STATUS_CHIP = {
  [TECHNICIAN_STATUS.ACTIVE]: { bg: "#dcfce7", color: "#16a34a" },
  [TECHNICIAN_STATUS.INACTIVE]: { bg: "#fee2e2", color: "#dc2626" },
  [TECHNICIAN_STATUS.ON_LEAVE]: { bg: "#fef3c7", color: "#d97706" },
};

const INFO_ROWS = [
  { key: "phone", label: "Teléfono", icon: PhoneIcon, get: (t) => t.phone },
  { key: "vehicle", label: "Vehículo", icon: DirectionsCarIcon, get: (t) => t.assignedVehicle },
  { key: "zone", label: "Zona", icon: LocationOnIcon, get: (t) => t.zone },
  { key: "user", label: "Usuario", icon: PersonIcon, get: (t) => t.user?.name },
  { key: "email", label: "Email", icon: EmailIcon, get: (t) => t.user?.email },
];

function CompactInfoPanel({ technician }) {
  return (
    <Box
      sx={{
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        bgcolor: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${C.border}`, bgcolor: "#fafafa" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.text }}>
          Datos de contacto
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", ...SCROLL }}>
        {INFO_ROWS.map(({ key, label, icon: Icon, get }) => (
          <Box
            key={key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.85,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <Icon sx={{ fontSize: 15, color: C.primary, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, color: C.textSoft, width: 62, flexShrink: 0 }}>
              {label}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: C.text, flex: 1 }}>
              {get(technician) || "—"}
            </Typography>
          </Box>
        ))}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.85,
          }}
        >
          <SummarizeIcon sx={{ fontSize: 15, color: C.primary }} />
          <Typography sx={{ fontSize: 11, color: C.textSoft, width: 62 }}>Estado</Typography>
          <Chip
            size="small"
            label={STATUS_LABELS[technician.status]}
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: STATUS_CHIP[technician.status]?.bg,
              color: STATUS_CHIP[technician.status]?.color,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

function ResumenTab({ technician, onViewInventory }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadExpediente = async () => {
    try {
      setDownloading(true);
      await technicianExpedienteService.downloadExpediente(technician._id);
    } catch (err) {
      console.error("Error downloading expediente:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 280px) minmax(0, 1fr)" },
        gridTemplateRows: { xs: "auto 1fr", md: "1fr" },
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          minHeight: 0,
        }}
      >
        <CompactInfoPanel technician={technician} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<InventoryIcon sx={{ fontSize: 16 }} />}
            onClick={() => onViewInventory(technician)}
            sx={{
              textTransform: "none",
              borderColor: C.border,
              color: C.text,
              fontWeight: 600,
              fontSize: 12,
              py: 0.75,
              "&:hover": { borderColor: C.primary, bgcolor: C.primarySoft },
            }}
          >
            Ver inventario
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleDownloadExpediente}
            disabled={downloading}
            startIcon={
              downloading ? (
                <CircularProgress size={14} />
              ) : (
                <FolderOpenIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, py: 0.75 }}
          >
            Descargar expediente
          </Button>
        </Box>
      </Box>

      <Box sx={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
        <TechnicianScoresChart technician={technician} fillHeight />
      </Box>
    </Box>
  );
}

export default function TechnicianDetailPanel({
  technician,
  onEdit,
  onDelete,
  onViewInventory,
  canEditExpediente = false,
}) {
  const [detailTab, setDetailTab] = useState(0);

  if (!technician) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: C.textSoft,
          gap: 1,
        }}
      >
        <PersonIcon sx={{ fontSize: 40, opacity: 0.3 }} />
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
          Selecciona un técnico del directorio
        </Typography>
      </Box>
    );
  }

  const statusStyle = STATUS_CHIP[technician.status] || STATUS_CHIP[TECHNICIAN_STATUS.ACTIVE];

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header compacto */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: C.primary, fontSize: 16, fontWeight: 700 }}>
            {technician.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 17, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
              {technician.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={STATUS_LABELS[technician.status]}
                sx={{
                  height: 20,
                  fontWeight: 700,
                  fontSize: 10,
                  bgcolor: statusStyle.bg,
                  color: statusStyle.color,
                }}
              />
              <Typography noWrap sx={{ fontSize: 11, color: C.textSoft }}>
                {technician.phone}
                {technician.zone ? ` · ${technician.zone}` : ""}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 0.75 }}>
            {[
              { label: "Actitud", value: technician.attitudeScore ?? "—", color: "#6366f1" },
              { label: "Admin", value: technician.adminComplianceScore ?? "—", color: "#8b5cf6" },
            ].map((k) => (
              <Box
                key={k.label}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.5,
                  border: `1px solid ${C.border}`,
                  bgcolor: "#fff",
                  textAlign: "center",
                  minWidth: 52,
                }}
              >
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: C.textSoft, textTransform: "uppercase" }}>
                  {k.label}
                </Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: k.color, lineHeight: 1.1 }}>
                  {k.value}
                </Typography>
              </Box>
            ))}
          </Box>
          <IconButton size="small" onClick={() => onEdit(technician)}>
            <EditIcon sx={{ fontSize: 18, color: C.primary }} />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(technician)}>
            <DeleteIcon sx={{ fontSize: 18, color: C.danger }} />
          </IconButton>
        </Box>
      </Box>

      {/* Sub-tabs */}
      <Box sx={{ borderBottom: `1px solid ${C.border}`, flexShrink: 0, px: 1.5 }}>
        <Tabs
          value={detailTab}
          onChange={(_, v) => setDetailTab(v)}
          sx={{
            minHeight: 38,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              minHeight: 38,
              py: 0.5,
            },
          }}
        >
          <Tab icon={<SummarizeIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Resumen" />
          <Tab icon={<FolderOpenIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Expediente" />
          <Tab icon={<InventoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Inventario" />
        </Tabs>
      </Box>

      {/* Contenido — sin scroll en resumen/expediente; solo inventario si hace falta */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: detailTab === 2 ? "auto" : "hidden",
          p: 1.5,
          bgcolor: "#fafafa",
        }}
      >
        {detailTab === 0 && (
          <ResumenTab technician={technician} onViewInventory={onViewInventory} />
        )}
        {detailTab === 1 && (
          <TechnicianExpedientePanel
            technician={technician}
            canEdit={canEditExpediente}
          />
        )}
        {detailTab === 2 && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              border: `1px solid ${C.border}`,
              bgcolor: "#fff",
              p: 2,
            }}
          >
            <InventoryIcon sx={{ fontSize: 36, color: C.primary, mb: 0.5, opacity: 0.7 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.text }}>
              Inventario de {technician.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.textSoft, mb: 1.5 }}>
              Consulta el stock asignado a este técnico
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<InventoryIcon />}
              onClick={() => onViewInventory(technician)}
              sx={{
                textTransform: "none",
                bgcolor: C.primary,
                fontWeight: 600,
                "&:hover": { bgcolor: C.primary },
              }}
            >
              Abrir inventario
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
